export function rawValue(value) {
  if (value == null) return "";
  if (typeof value === "object" && value.target && "value" in value.target) {
    return String(value.target.value ?? "");
  }
  if (typeof value === "object") {
    for (const key of ["raw", "text", "value", "latex", "expr"]) {
      if (value[key] != null && typeof value[key] !== "object") {
        return String(value[key]);
      }
    }
  }
  return String(value);
}

export function gcd(a, b) {
  let x = Math.abs(Math.round(a));
  let y = Math.abs(Math.round(b));
  while (y) {
    const t = x % y;
    x = y;
    y = t;
  }
  return x || 1;
}

export function simplifyFraction(num, den) {
  const sign = den < 0 ? -1 : 1;
  const g = gcd(num, den);
  return { num: (sign * num) / g, den: Math.abs(den) / g };
}

export function combo(n, k) {
  if (k < 0 || k > n) return 0;
  const r = Math.min(k, n - k);
  let out = 1;
  for (let i = 1; i <= r; i++) {
    out = (out * (n - r + i)) / i;
  }
  return Math.round(out);
}

export function decimalLatex(value) {
  return String(value).replace(".", ",").replace(",", "{,}");
}

export function fractionLatex(num, den) {
  const f = simplifyFraction(num, den);
  if (f.den === 1) return String(f.num);
  return `\\frac{${f.num}}{${f.den}}`;
}

export function normalizeMath(raw) {
  return rawValue(raw)
    .toLowerCase()
    .replace(/\\left|\\right/g, "")
    .replace(/\\cdot|\\times|·|×/g, "*")
    .replace(/\{,\}/g, ".")
    .replace(/,/g, ".")
    .replace(/\s+/g, "")
    .replace(/\|/g, "")
    .replace(/^p\([^)]*\)=/, "")
    .replace(/^p=/, "");
}

export function parseNumber(raw) {
  const original = rawValue(raw).trim();
  const hasPercent = /%/.test(original);
  let s = normalizeMath(original)
    .replace(/[^0-9.\-+/]/g, "");
  if (!s) return null;
  let value = null;
  if (/^-?\d+(\.\d+)?$/.test(s)) {
    value = Number(s);
  } else if (/^-?\d+(\.\d+)?\/-?\d+(\.\d+)?$/.test(s)) {
    const [a, b] = s.split("/").map(Number);
    value = b === 0 ? null : a / b;
  }
  if (value == null || !Number.isFinite(value)) return null;
  return hasPercent ? value / 100 : value;
}

export function evalExpression(raw) {
  let s = normalizeMath(raw);
  s = s.replace(/(\d+(?:\.\d+)?)%/g, "($1/100)");
  s = s.replace(/\\binom\{(\d+)\}\{(\d+)\}/g, (_, n, k) => String(combo(Number(n), Number(k))));
  s = s.replace(/c\((\d+)[,.](\d+)\)/g, (_, n, k) => String(combo(Number(n), Number(k))));
  s = s.replace(/\((\d+)po(\d+)\)/g, (_, n, k) => String(combo(Number(n), Number(k))));
  s = s.replace(/[{}]/g, "");
  s = s.replace(/\^/g, "**");
  if (!/^[0-9+\-*/().*]+$/.test(s)) return null;
  try {
    // eslint-disable-next-line no-new-func
    const value = Function(`"use strict"; return (${s});`)();
    return Number.isFinite(value) ? value : null;
  } catch (_) {
    return null;
  }
}

export function isValueCorrect(raw, expected, tolerance = 1e-9) {
  const parsed = parseNumber(raw);
  if (parsed != null && Math.abs(parsed - expected) <= tolerance) return true;
  const evaluated = evalExpression(raw);
  return evaluated != null && Math.abs(evaluated - expected) <= tolerance;
}

export function toLatexExpression(raw) {
  const s = rawValue(raw).trim();
  if (!s) return "?";
  return s
    .replace(/,/g, "{,}")
    .replace(/\*/g, "\\cdot ")
    .replace(/\^\(([^)]+)\)/g, "^{$1}")
    .replace(/\^(\d+)/g, "^{$1}")
    .replace(/\\binom/g, "\\binom");
}

export function scoreParts(parts) {
  const score = parts.filter((p) => p.correct).length;
  return {
    isCorrect: score === parts.length,
    correct: score === parts.length,
    score,
    maxScore: parts.length,
    parts,
  };
}

export function setField(answer, key, value, onChange) {
  onChange && onChange({ ...(answer || {}), [key]: value });
}
